import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Loader2, Trash2, GripVertical, X, Edit3, Check, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = '/api';

const SortableImageItem = ({ media, onDelete, onUpdateMetadata, token }) => {
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [altText, setAltText] = useState(media.alt_text || '');
  const [caption, setCaption] = useState(media.caption || '');
  const [category, setCategory] = useState(media.category || '');
  const [isSaving, setIsSaving] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Load Base64 image data
  useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await axios.get(`${API}/media/${media.id}/base64`);
        setImageData(response.data.data_url);
      } catch (error) {
        console.error('Failed to load image:', error);
      } finally {
        setIsLoadingImage(false);
      }
    };
    loadImage();
  }, [media.id]);

  // Category suggestions for AI search
  const categoryOptions = [
    'Pool', 'Garden', 'Bedroom', 'Bathroom', 'Kitchen', 'Living Room', 
    'Terrace', 'Exterior', 'View', 'Dining', 'Entrance', 'Amenities'
  ];

  const handleSaveMetadata = async () => {
    setIsSaving(true);
    try {
      const [username, password] = token.split(':');
      await axios.put(
        `${API}/media/${media.id}/metadata`,
        { alt_text: altText, caption, category },
        { auth: { username, password } }
      );
      onUpdateMetadata(media.id, { alt_text: altText, caption, category });
      toast.success('Metadata saved');
      setIsEditingMeta(false);
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Failed to save metadata');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`media-item-${media.id}`}>
      <div className="relative aspect-square overflow-hidden rounded-sm border-2 border-border hover:border-primary transition-all">
        {isLoadingImage ? (
          <div className="w-full h-full flex items-center justify-center bg-stone-100">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : imageData ? (
          <img
            src={imageData}
            alt={media.alt_text || media.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-stone-100 text-muted-foreground text-sm">
            Image unavailable
          </div>
        )}
        
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 bg-black/50 hover:bg-black/70 p-2 rounded-sm cursor-grab active:cursor-grabbing transition-all"
          data-testid="drag-handle"
        >
          <GripVertical className="text-white" size={20} />
        </div>
        
        {/* Delete button */}
        <button
          onClick={() => onDelete(media.id)}
          className="absolute top-2 right-2 bg-destructive hover:bg-destructive/90 text-white p-2 rounded-sm transition-all opacity-0 group-hover:opacity-100"
          data-testid={`delete-button-${media.id}`}
        >
          <Trash2 size={16} />
        </button>

        {/* Metadata edit button */}
        <button
          onClick={() => setIsEditingMeta(true)}
          className="absolute top-2 right-12 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-sm transition-all opacity-0 group-hover:opacity-100"
          title="Edit AI/SEO metadata"
          data-testid={`metadata-button-${media.id}`}
        >
          <FileText size={16} />
        </button>
        
        {/* Info bar with category badge */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2">
          <p className="text-sm truncate font-medium">{media.title}</p>
          <div className="flex items-center gap-2 mt-1">
            {media.category && (
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{media.category}</span>
            )}
            {media.alt_text && (
              <p className="text-xs text-white/60 truncate flex-1">{media.alt_text}</p>
            )}
          </div>
        </div>
      </div>

      {/* Metadata editing modal */}
      {isEditingMeta && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setIsEditingMeta(false)}>
          <div 
            className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-semibold text-lg mb-2">Edit AI & SEO Metadata</h4>
            <p className="text-sm text-muted-foreground mb-4">
              These fields help search engines and AI assistants understand your images.
            </p>
            {imageData && (
              <img
                src={imageData}
                alt={media.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
            )}
            
            {/* Category */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="category-select"
              >
                <option value="">Select a category</option>
                {categoryOptions.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Helps guests filter images</p>
            </div>
            
            {/* Alt Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Alt Text (SEO)</label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="e.g., Luxury villa pool surrounded by Mediterranean garden"
                data-testid="alt-text-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Describes the image for search engines & accessibility</p>
            </div>
            
            {/* Caption */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Caption (AI Search)</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g., The heated infinity pool overlooks the Adriatic Sea, perfect for sunset swims."
                className="w-full p-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[80px]"
                data-testid="caption-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Detailed description for AI search discovery</p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setIsEditingMeta(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveMetadata}
                disabled={isSaving}
                className="bg-primary"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Metadata
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MediaManagement = ({ token }) => {
  const [media, setMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileTitles, setFileTitles] = useState({});
  const [fileAltTexts, setFileAltTexts] = useState({});
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchMedia = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data);
      setRefreshKey(Date.now());
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    const titles = {};
    const altTexts = {};
    files.forEach((file, idx) => {
      titles[idx] = file.name.split('.')[0];
      altTexts[idx] = '';
    });
    setFileTitles(titles);
    setFileAltTexts(altTexts);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      Object.values(fileTitles).forEach((title) => {
        formData.append('titles', title);
      });

      const [username, password] = token.split(':');
      const response = await axios.post(`${API}/media/upload`, formData, {
        auth: { username, password },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${response.data.uploaded} images uploaded successfully`);
      setSelectedFiles([]);
      setFileTitles({});
      setFileAltTexts({});
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const [username, password] = token.split(':');
      await axios.delete(`${API}/media/${mediaId}`, {
        auth: { username, password },
      });

      toast.success('Image deleted successfully');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleUpdateMetadata = (mediaId, metadata) => {
    setMedia(prev => prev.map(item => 
      item.id === mediaId ? { ...item, ...metadata } : item
    ));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = media.findIndex((item) => item.id === active.id);
    const newIndex = media.findIndex((item) => item.id === over.id);

    const newMedia = arrayMove(media, oldIndex, newIndex);
    setMedia(newMedia);

    try {
      const [username, password] = token.split(':');
      const mediaIds = newMedia.map((item) => item.id);
      
      await axios.put(
        `${API}/media/reorder`,
        { media_ids: mediaIds },
        { auth: { username, password } }
      );

      toast.success('Gallery order updated');
    } catch (error) {
      console.error('Error reordering media:', error);
      toast.error('Failed to update order');
      fetchMedia();
    }
  };

  const handleCancelUpload = () => {
    setSelectedFiles([]);
    setFileTitles({});
    setFileAltTexts({});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="media-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="media-management">
      <Card className="border-border" data-testid="upload-section">
        <CardContent className="p-6">
          <h3 className="font-playfair text-2xl font-bold mb-4">Upload New Images</h3>
          <p className="text-muted-foreground mb-6">
            Upload high-resolution images. They will be automatically optimized to WebP format for fast loading.
          </p>

          {selectedFiles.length === 0 ? (
            <div className="border-2 border-dashed border-border rounded-sm p-12 text-center hover:border-primary transition-all">
              <label htmlFor="file-upload" className="cursor-pointer" data-testid="upload-dropzone">
                <Upload className="mx-auto mb-4 text-muted-foreground" size={48} />
                <p className="text-lg font-medium mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-muted-foreground">JPG, PNG, or WebP (max 10MB per file)</p>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
              </label>
            </div>
          ) : (
            <div className="space-y-4" data-testid="upload-preview">
              <div className="flex items-center justify-between mb-4">
                <p className="font-medium">{selectedFiles.length} files selected</p>
                <Button variant="outline" size="sm" onClick={handleCancelUpload} data-testid="cancel-upload-button">
                  <X size={16} className="mr-2" />
                  Cancel
                </Button>
              </div>

              {selectedFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 border border-border rounded-sm">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-20 h-20 object-cover rounded-sm"
                  />
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Image title"
                      value={fileTitles[idx] || ''}
                      onChange={(e) => setFileTitles({ ...fileTitles, [idx]: e.target.value })}
                      className="rounded-sm"
                      data-testid={`title-input-${idx}`}
                    />
                    <Input
                      placeholder="SEO alt text (optional)"
                      value={fileAltTexts[idx] || ''}
                      onChange={(e) => setFileAltTexts({ ...fileAltTexts, [idx]: e.target.value })}
                      className="rounded-sm text-sm"
                      data-testid={`alt-input-${idx}`}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              ))}

              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="w-full bg-primary hover:bg-primary/90 rounded-sm"
                data-testid="upload-button"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading & Optimizing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload {selectedFiles.length} {selectedFiles.length === 1 ? 'Image' : 'Images'}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div data-testid="gallery-section">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-playfair text-2xl font-bold">Gallery Images</h3>
            <p className="text-muted-foreground">
              Drag to reorder. Click <FileText className="inline h-4 w-4" /> to edit AI/SEO metadata.
            </p>
          </div>
          <span className="text-muted-foreground">{media.length} images</span>
        </div>

        {media.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">No images uploaded yet. Upload your first image above.</p>
            </CardContent>
          </Card>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={media.map((item) => item.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
                {media.map((item) => (
                  <SortableImageItem 
                    key={item.id} 
                    media={item} 
                    onDelete={handleDelete}
                    onUpdateMetadata={handleUpdateMetadata}
                    token={token}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default MediaManagement;
